/* Copyright (c) Trimble Inc. All rights reserved. */

import { Logger } from '../logger';
import { HttpError } from '../api-client/HttpError';

// Framework-agnostic primitive for recording Gainsight PX custom events; no-op when the SDK is absent.

type AptrinsicFn = ((...args: unknown[]) => void) & {
  q?: unknown[][];
  p?: string;
  c?: unknown;
};

interface AptrinsicWindow extends Window {
  aptrinsic?: AptrinsicFn;
}

/** Custom-event properties accepted by Gainsight PX `track`. */
export type GainsightEventProperties = Record<string, string | number | boolean | null | undefined>;

function getAptrinsic(): AptrinsicFn | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  // `window` is assignable to AptrinsicWindow (aptrinsic is optional), so no cast.
  const aptrinsicWindow: AptrinsicWindow = window;
  return aptrinsicWindow.aptrinsic;
}

export function trackGainsightEvent(
  eventName: string,
  properties?: GainsightEventProperties,
): void {
  // Reject whitespace-only names rather than sending a blank event.
  const trimmedEventName = eventName?.trim();
  if (!trimmedEventName) {
    Logger.warn('Gainsight: trackGainsightEvent called without an event name, ignoring');
    return;
  }

  const aptrinsic = getAptrinsic();
  if (!aptrinsic) {
    // SDK not present: disabled, not yet loaded, or non-browser context.
    return;
  }

  try {
    if (properties) {
      aptrinsic('track', trimmedEventName, properties);
    } else {
      aptrinsic('track', trimmedEventName);
    }
  } catch (error) {
    Logger.error('Gainsight: track call failed', error);
  }
}

// Stable, low-cardinality failure reason for analytics; keeps raw messages out of events.
export function toGainsightErrorReason(error: unknown): string {
  return error instanceof HttpError ? `http_${error.status}` : 'unknown';
}
